import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Box,
  Alert,
  AlertTitle,
  Chip,
  Divider,
  Typography,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BoltIcon from "@mui/icons-material/Bolt";
import BuildIcon from "@mui/icons-material/Build";
import { UploadDropzone } from "./UploadDropzone";
import { preventiviApi } from "../../services/preventiviApi";
import { extractorApi, ExtractionPreviewResult } from "../../services/extractorApi";
import { CATEGORIES } from "../../services/configApi";
import toast from "react-hot-toast";
import { getCurrentYearRome } from "../../utils/dateFormat";
import { downloadGaraTemplate } from "../../utils/garaTemplate";

type Step = "form" | "extracting" | "preview" | "creating";

interface UploadDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

export const UploadDialog: React.FC<UploadDialogProps> = ({ open, onClose, onCreated }) => {
  const [step, setStep] = useState<Step>("form");

  // File state: separate ELE and MEC files, plus legacy single file
  const [fileEle, setFileEle] = useState<File | null>(null);
  const [fileMec, setFileMec] = useState<File | null>(null);

  // Global metadata
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [categoria, setCategoria] = useState("");
  const [anno, setAnno] = useState<string>("");
  const [nomeCantiere, setNomeCantiere] = useState("");
  const [codiceProgetto, setCodiceProgetto] = useState("");

  const [extractError, setExtractError] = useState<string | null>(null);
  const [previewEle, setPreviewEle] = useState<ExtractionPreviewResult | null>(null);
  const [previewMec, setPreviewMec] = useState<ExtractionPreviewResult | null>(null);
  const [debugDownloading, setDebugDownloading] = useState<"ele" | "mec" | null>(null);

  // Convenience: at least one preview available
  const preview = previewEle ?? previewMec;

  const isLoading = step === "extracting" || step === "creating";
  const hasAnyFile = fileEle !== null || fileMec !== null;

  const reset = () => {
    setStep("form");
    setFileEle(null);
    setFileMec(null);
    setName("");
    setDescription("");
    setCategoria("");
    setAnno("");
    setNomeCantiere("");
    setCodiceProgetto("");
    setExtractError(null);
    setPreviewEle(null);
    setPreviewMec(null);
    setDebugDownloading(null);
  };

  const handleClose = () => {
    if (!isLoading) { reset(); onClose(); }
  };

  const handleBack = () => {
    setStep("form");
    setExtractError(null);
    setPreviewEle(null);
    setPreviewMec(null);
  };

  const handleAnalyze = async () => {
    if (!name.trim()) {
      toast.error("Inserisci un nome per il preventivo");
      return;
    }

    if (!fileEle && !fileMec) {
      await handleCreate();
      return;
    }

    setExtractError(null);
    setStep("extracting");
    try {
      const [resEle, resMec] = await Promise.all([
        fileEle ? extractorApi.preview(fileEle) : null,
        fileMec ? extractorApi.preview(fileMec) : null,
      ]);
      const totalItems = (resEle?.total_items ?? 0) + (resMec?.total_items ?? 0);
      if (totalItems === 0) {
        setStep("form");
        setExtractError("Nessun elemento trovato nei file. Verifica che i file contengano righe con Keyword/Descrizione e Quantità.");
        return;
      }
      setPreviewEle(resEle);
      setPreviewMec(resMec);
      setStep("preview");
    } catch (e: any) {
      setStep("form");
      setExtractError(e?.response?.data?.detail || "Errore nell'analisi del file.");
    }
  };

  const handleCreate = async () => {
    setStep("creating");
    try {
      const p = await preventiviApi.create({
        name: name.trim(),
        description: description.trim() || undefined,
        categoria: categoria || undefined,
        anno: anno ? parseInt(anno) : undefined,
        nome_cantiere: nomeCantiere.trim() || undefined,
        codice_progetto: codiceProgetto.trim() || undefined,
        // When both disciplines have previews, merge items keeping discipline order;
        // otherwise fall back to raw files so the backend tags them correctly.
        ...(previewEle && previewMec
          ? {
              items_json: JSON.stringify([
                ...previewEle.items.map((item) => ({ ...item, disciplina: "ELE" })),
                ...previewMec.items.map((item) => ({ ...item, disciplina: "MEC" })),
              ]),
            }
          : previewEle
            ? { items_json: JSON.stringify(previewEle.items.map((item) => ({ ...item, disciplina: "ELE" }))) }
            : previewMec
              ? { items_json: JSON.stringify(previewMec.items.map((item) => ({ ...item, disciplina: "MEC" }))) }
              : fileEle || fileMec
                ? { file_ele: fileEle ?? undefined, file_mec: fileMec ?? undefined }
                : {}),
      });
      toast.success("Preventivo creato");
      reset();
      onCreated(p.id);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Errore nella creazione del preventivo");
      setStep(preview ? "preview" : "form");
    }
  };

  const handleDownloadDebug = async (discipline: "ele" | "mec") => {
    const p = discipline === "ele" ? previewEle : previewMec;
    if (!p) return;
    setDebugDownloading(discipline);
    try {
      await extractorApi.downloadDebug(p.temp_id, `debug_${discipline}_${name.trim().slice(0, 20)}.xlsx`);
    } catch {
      toast.error("Errore nel download");
    } finally {
      setDebugDownloading(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "1rem" } }}
    >
      <DialogTitle sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
        {step === "preview" && (
          <IconButton size="small" onClick={handleBack} sx={{ mr: 0.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        {step === "preview" ? "Anteprima estrazione" : "Nuovo preventivo"}
      </DialogTitle>

      <DialogContent>
        {step === "form" || step === "extracting" || (!preview && step === "creating") ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {extractError && (
              <Alert severity="error" onClose={() => setExtractError(null)}>
                <AlertTitle>Formato non riconosciuto</AlertTitle>
                {extractError}
              </Alert>
            )}

            {/* ── Global metadata ─────────────────────────────────────────── */}
            <TextField
              label="Nome preventivo *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              disabled={isLoading}
              size="small"
              placeholder="es. Preventivo impianto A – 2026"
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl size="small" sx={{ flex: 2 }}>
                <InputLabel>Categoria *</InputLabel>
                <Select
                  value={categoria}
                  label="Categoria *"
                  onChange={(e) => setCategoria(e.target.value)}
                  disabled={isLoading}
                >
                  <MenuItem value=""><em>—</em></MenuItem>
                  {CATEGORIES.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Anno"
                value={anno}
                onChange={(e) => setAnno(e.target.value)}
                size="small"
                type="number"
                sx={{ flex: 1 }}
                disabled={isLoading}
                placeholder={String(getCurrentYearRome())}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Nome cantiere"
                value={nomeCantiere}
                onChange={(e) => setNomeCantiere(e.target.value)}
                fullWidth
                size="small"
                disabled={isLoading}
              />
              <TextField
                label="Codice progetto"
                value={codiceProgetto}
                onChange={(e) => setCodiceProgetto(e.target.value)}
                fullWidth
                size="small"
                disabled={isLoading}
              />
            </Box>

            <TextField
              label="Note (opzionale)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              disabled={isLoading}
              size="small"
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
                p: 1.5,
                borderRadius: "10px",
                bgcolor: "#F5F7FA",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: "1.1rem", color: "text.secondary", mt: 0.15, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.5 }}>
                  I file di gara vanno caricati seguendo il template standard con le colonne{" "}
                  <strong>Capitolo</strong>, <strong>Codice</strong>, <strong>Descrizione breve</strong>,{" "}
                  <strong>Descrizione estesa</strong>, <strong>UdM</strong> e <strong>Quantità</strong>{" "}
                  (Importo e Importo totale sono opzionali).
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon sx={{ fontSize: "1rem !important" }} />}
                  onClick={downloadGaraTemplate}
                  disabled={isLoading}
                  sx={{ mt: 1, textTransform: "none", fontSize: "0.75rem", py: 0.25 }}
                >
                  Scarica template Excel
                </Button>
              </Box>
            </Box>

            <Divider />

            {/* ── File sections ─────────────────────────────────────────────── */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                  <BoltIcon sx={{ fontSize: "1rem", color: "#1565C0" }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#1565C0", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Sezione Elettrica (ELE)
                  </Typography>
                  <Typography variant="caption" color="text.disabled">— opzionale</Typography>
                </Box>
                <UploadDropzone
                  file={fileEle}
                  onChange={(f) => { setFileEle(f); setExtractError(null); }}
                  disabled={isLoading}
                  placeholder="Trascina il file BOM elettrico (.xlsx, .xls)"
                />
              </Box>

              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                  <BuildIcon sx={{ fontSize: "1rem", color: "#E65100" }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#E65100", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Sezione Meccanica (MEC)
                  </Typography>
                  <Typography variant="caption" color="text.disabled">— opzionale</Typography>
                </Box>
                <UploadDropzone
                  file={fileMec}
                  onChange={(f) => { setFileMec(f); setExtractError(null); }}
                  disabled={isLoading}
                  placeholder="Trascina il file BOM meccanico (.xlsx, .xls)"
                />
              </Box>
            </Box>
          </Box>
        ) : (
          /* ── Preview step ─────────────────────────────────────────────────── */
          <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Summary header */}
            <Box
              sx={{
                borderRadius: "12px", border: "1.5px solid", borderColor: "success.light",
                bgcolor: "#F1F8F4", p: 2.5, display: "flex", flexDirection: "column", gap: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircleOutlineIcon sx={{ color: "success.main", fontSize: "1.4rem" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "success.dark" }}>
                  Estrazione completata
                </Typography>
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.1 }}>
                  {(previewEle?.total_items ?? 0) + (previewMec?.total_items ?? 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">elementi estratti totali</Typography>
              </Box>

              {/* Per-discipline breakdown */}
              {(previewEle || previewMec) && (
                <>
                  <Divider />
                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                    {previewEle && (
                      <Box
                        sx={{
                          flex: 1, minWidth: 130, borderRadius: "8px",
                          border: "1px solid #BBDEFB", bgcolor: "#E3F2FD", p: 1.25,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                          <BoltIcon sx={{ fontSize: "0.9rem", color: "#1565C0" }} />
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "#1565C0", textTransform: "uppercase", letterSpacing: 0.4 }}>
                            ELE
                          </Typography>
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1, color: "#1565C0" }}>
                          {previewEle.total_items}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">elementi</Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
                          {previewEle.headers_found > 0 && (
                            <Chip label={`${previewEle.headers_found} sez.`} size="small" sx={{ height: 18, fontSize: "0.65rem", bgcolor: "#BBDEFB", color: "#1565C0", fontWeight: 600 }} />
                          )}
                          {previewEle.multirow_groups > 0 && (
                            <Chip label={`${previewEle.multirow_groups} multiriga`} size="small" sx={{ height: 18, fontSize: "0.65rem", bgcolor: "#F3E5F5", color: "#7B1FA2", fontWeight: 600 }} />
                          )}
                          {previewEle.rows_skipped > 0 && (
                            <Chip label={`${previewEle.rows_skipped} ignorate`} size="small" sx={{ height: 18, fontSize: "0.65rem" }} />
                          )}
                        </Box>
                      </Box>
                    )}
                    {previewMec && (
                      <Box
                        sx={{
                          flex: 1, minWidth: 130, borderRadius: "8px",
                          border: "1px solid #FFCCBC", bgcolor: "#FBE9E7", p: 1.25,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                          <BuildIcon sx={{ fontSize: "0.9rem", color: "#E65100" }} />
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "#E65100", textTransform: "uppercase", letterSpacing: 0.4 }}>
                            MEC
                          </Typography>
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1, color: "#E65100" }}>
                          {previewMec.total_items}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">elementi</Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
                          {previewMec.headers_found > 0 && (
                            <Chip label={`${previewMec.headers_found} sez.`} size="small" sx={{ height: 18, fontSize: "0.65rem", bgcolor: "#FFCCBC", color: "#E65100", fontWeight: 600 }} />
                          )}
                          {previewMec.multirow_groups > 0 && (
                            <Chip label={`${previewMec.multirow_groups} multiriga`} size="small" sx={{ height: 18, fontSize: "0.65rem", bgcolor: "#F3E5F5", color: "#7B1FA2", fontWeight: 600 }} />
                          )}
                          {previewMec.rows_skipped > 0 && (
                            <Chip label={`${previewMec.rows_skipped} ignorate`} size="small" sx={{ height: 18, fontSize: "0.65rem" }} />
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Preventivo: <strong>{name}</strong>
              {categoria && ` · ${categoria}`}
              {anno && ` · ${anno}`}
              {nomeCantiere && ` · ${nomeCantiere}`}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexWrap: "wrap" }}>
        {(step === "preview" || (step === "creating" && preview)) ? (
          <>
            <Button onClick={handleClose} disabled={isLoading}>Annulla</Button>
            {previewEle && (
              <Button
                variant="outlined"
                size="small"
                startIcon={debugDownloading === "ele" ? <CircularProgress size={14} /> : <DownloadIcon />}
                onClick={() => handleDownloadDebug("ele")}
                disabled={debugDownloading !== null || isLoading}
                sx={{ gap: 0.5 }}
              >
                Debug ELE
              </Button>
            )}
            {previewMec && (
              <Button
                variant="outlined"
                size="small"
                startIcon={debugDownloading === "mec" ? <CircularProgress size={14} /> : <DownloadIcon />}
                onClick={() => handleDownloadDebug("mec")}
                disabled={debugDownloading !== null || isLoading}
              >
                Debug MEC
              </Button>
            )}
            <Button variant="contained" onClick={handleCreate} disabled={isLoading}>
              {isLoading ? <CircularProgress size={22} /> : "Prosegui al preventivo →"}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} disabled={isLoading}>Annulla</Button>
            <Button
              onClick={handleAnalyze}
              variant="contained"
              disabled={isLoading || !name.trim()}
            >
              {step === "extracting" || step === "creating"
                ? <CircularProgress size={22} />
                : hasAnyFile ? "Analizza file" : "Crea preventivo"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
